import { isStandalone, getStandaloneData } from './config.js';

async function parseResponse(res: Response): Promise<unknown> {
  const text = await res.text();
  let json: { data?: unknown; error?: string };
  try {
    json = JSON.parse(text);
  } catch (err) {
    throw new Error(res.ok ? `Invalid JSON from server: ${(err as Error).message}` : `Server error (${res.status})`);
  }
  if (!res.ok) {
    throw new Error(json.error || 'Request failed');
  }
  return json.data;
}

async function request(method: string, path: string, body?: unknown): Promise<unknown> {
  const opts: RequestInit = {
    method,
    headers: {} as Record<string, string>,
    credentials: 'same-origin',
  };

  if (body) {
    (opts.headers as Record<string, string>)['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(`/api${path}`, opts);
  return parseResponse(res);
}

async function uploadFile(apiPath: string, file: File): Promise<unknown> {
  const formData = new FormData();
  formData.append('image', file);
  const res = await fetch(`/api${apiPath}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  });
  return parseResponse(res);
}

// --- Standalone data helpers ---

function sd(): Record<string, unknown> {
  return getStandaloneData() as Record<string, unknown>;
}

export const api = {
  // Auth
  login: (email: string, password: string) => request('POST', '/auth/login', { email, password }),
  logout: () => request('POST', '/auth/logout'),
  me: () => {
    if (isStandalone()) return Promise.resolve({ user: null });
    return request('GET', '/auth/me') as Promise<{ user: { id: number; username: string; is_admin: number } | null }>;
  },

  // Concepts
  getConcepts: () => {
    if (isStandalone()) return Promise.resolve(sd().conceptsList);
    return request('GET', '/concepts');
  },
  getConcept: (slug: string) => {
    if (isStandalone()) return Promise.resolve((sd().concepts as Record<string, unknown>)[slug]);
    return request('GET', `/concepts/${slug}`);
  },

  // Graph
  getGlobalGraph: () => {
    if (isStandalone()) return Promise.resolve(sd().globalGraph);
    return request('GET', '/graph/global');
  },
  getTreeGraph: () => {
    if (isStandalone()) return Promise.resolve(sd().treeGraph);
    return request('GET', '/graph/tree');
  },
  getTimelineGraph: () => {
    if (isStandalone()) return Promise.resolve(sd().timelineGraph);
    return request('GET', '/graph/timeline');
  },

  // Admin
  getRelationTypes: () => request('GET', '/admin/relation-types'),
  createRelationType: (data: unknown) => request('POST', '/admin/relation-types', data),
  updateRelationType: (id: number, data: unknown) => request('PUT', `/admin/relation-types/${id}`, data),
  deleteRelationType: (id: number) => request('DELETE', `/admin/relation-types/${id}`),
  createConcept: (data: unknown) => request('POST', '/admin/concepts', data),
  updateConcept: (slug: string, data: unknown) => request('PUT', `/admin/concepts/${slug}`, data),
  getCrosslinks: () => request('GET', '/admin/crosslinks'),
  getConceptCrosslinks: (slug: string) => request('GET', `/admin/crosslinks/by-concept/${encodeURIComponent(slug)}`),
  createCrosslink: (data: unknown) => request('POST', '/admin/crosslinks', data),
  updateCrosslink: (id: number, data: unknown) => request('PUT', `/admin/crosslinks/${id}`, data),
  deleteCrosslink: (id: number) => request('DELETE', `/admin/crosslinks/${id}`),
  lookupCrosslink: (slugA: string, slugB: string) => request('GET', `/admin/crosslinks/lookup?a=${encodeURIComponent(slugA)}&b=${encodeURIComponent(slugB)}`),
  lookupCrosslinkById: (id: number) => request('GET', `/admin/crosslinks/${id}`),
  deleteConcept: (slug: string) => request('DELETE', `/admin/concepts/${slug}`),

  getUnlinkedConcepts: () => request('GET', '/admin/unlinked-concepts'),

  // Tags (admin)
  getTags: () => request('GET', '/admin/tags'),
  createTag: (name: string, hue: number) => request('POST', '/admin/tags', { name, hue }),
  updateTag: (id: number, data: unknown) => request('PUT', `/admin/tags/${id}`, data),
  deleteTag: (id: number) => request('DELETE', `/admin/tags/${id}`),
  assignTagToConcept: (slug: string, tagId: number) => request('POST', `/admin/concepts/${slug}/tags/${tagId}`),
  removeTagFromConcept: (slug: string, tagId: number) => request('DELETE', `/admin/concepts/${slug}/tags/${tagId}`),

  // Images (admin)
  uploadConceptImage: (slug: string, file: File) => uploadFile(`/admin/concepts/${slug}/image`, file),
  deleteConceptImage: (slug: string) => request('DELETE', `/admin/concepts/${slug}/image`),
  uploadCrosslinkImage: (id: number, file: File) => uploadFile(`/admin/crosslinks/${id}/image`, file),
  deleteCrosslinkImage: (id: number) => request('DELETE', `/admin/crosslinks/${id}/image`),

  // Aliases
  getConceptAliases: (slug: string) => request('GET', `/admin/concepts/${slug}/aliases`),
  addConceptAlias: (slug: string, alias: string) => request('POST', `/admin/concepts/${slug}/aliases`, { alias }),
  deleteConceptAlias: (slug: string, id: number) => request('DELETE', `/admin/concepts/${slug}/aliases/${id}`),

  // Translations
  getConceptTranslations: (slug: string) => request('GET', `/admin/concepts/${slug}/translations`),
  setConceptTranslation: (slug: string, lang: string, data: unknown) => request('PUT', `/admin/concepts/${slug}/translations/${lang}`, data),
  deleteConceptTranslation: (slug: string, lang: string) => request('DELETE', `/admin/concepts/${slug}/translations/${lang}`),
  setRelationTypeTranslation: (id: number, lang: string, data: unknown) => request('PUT', `/admin/relation-types/${id}/translations/${lang}`, data),
  getRelationTypeTranslations: (id: number) => request('GET', `/admin/relation-types/${id}/translations`),
  deleteRelationTypeTranslation: (id: number, lang: string) => request('DELETE', `/admin/relation-types/${id}/translations/${lang}`),
  getUntranslatedConcepts: (lang: string) => request('GET', `/admin/untranslated/${lang}`),
  getUntranslatedRelationTypes: (lang: string) => request('GET', `/admin/untranslated-relation-types/${lang}`),

  // Analytics (admin)
  getAnalytics: (period?: string) => request('GET', `/admin/analytics?period=${encodeURIComponent(period || '7d')}`),

  // Recent
  getRecent: (page = 1, limit = 20) => {
    if (isStandalone()) {
      const data = sd().recent as { items: unknown[]; total: number };
      const start = (page - 1) * limit;
      return Promise.resolve({
        items: data.items.slice(start, start + limit),
        page,
        limit,
        total: data.total,
      });
    }
    return request('GET', `/recent?page=${page}&limit=${limit}`);
  },

  // Flow (public)
  getFlowList: () => {
    if (isStandalone()) return Promise.resolve(sd().flowList);
    return request('GET', '/flow/list');
  },
  getFlow: (slug: string) => {
    if (isStandalone()) return Promise.resolve((sd().flows as Record<string, unknown>)[slug]);
    return request('GET', `/flow/${slug}`);
  },
  hasFlow: (slug: string) => {
    if (isStandalone()) return Promise.resolve({ hasFlow: !!(sd().hasFlow as Record<string, boolean>)[slug] });
    return request('GET', `/flow/${slug}/has-flow`);
  },

  // Flow (admin)
  createFlowStep: (slug: string, data: unknown) => request('POST', `/flow/admin/${slug}/steps`, data),
  updateFlowStep: (id: number, data: unknown) => request('PUT', `/flow/admin/steps/${id}`, data),
  deleteFlowStep: (id: number) => request('DELETE', `/flow/admin/steps/${id}`),
  createFlowEdge: (slug: string, data: unknown) => request('POST', `/flow/admin/${slug}/edges`, data),
  updateFlowEdge: (id: number, data: unknown) => request('PUT', `/flow/admin/edges/${id}`, data),
  deleteFlowEdge: (id: number) => request('DELETE', `/flow/admin/edges/${id}`),

  // Export (admin, all environments)
  adminExport: () => request('GET', '/admin/export'),
  adminExportObsidian: async () => {
    const res = await fetch('/api/admin/export/obsidian', {
      method: 'GET',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      let msg = 'Export failed';
      try { const json = await res.json(); msg = json.error || msg; } catch { /* not JSON */ }
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    a.download = match?.[1] || `crosslink-obsidian-${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  adminExportStandalone: async () => {
    const res = await fetch('/api/admin/export/standalone', {
      method: 'GET',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      let msg = 'Export failed';
      try { const json = await res.json(); msg = json.error || msg; } catch { /* not JSON */ }
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    a.download = match?.[1] || `crosslink-standalone-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Dev tools (dev environment only)
  devStatus: () => request('GET', '/dev/status'),
  devImport: (data: unknown) => request('POST', '/dev/import', data),
};
